import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	Optional,
	Output
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SocialListeningPlatformIconComponent } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/mention-cards-section/social-listening-platform-icon.component';
import { SocialListeningPlatformType } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/filters/platform-chips/platform-chip.component';
import { normalizeSocialListeningPlatform } from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-mentions.model';
import { formatPlatformLabel } from '@brand-lolly/app/social-media-influencer/modules/social-listening/utils/analytics-label.utils';
import { ShortNumberPipe, TypographyComponent } from 'components';
import { ReportBlockCreator } from '../../../../services/report-blocks.service';
import { discoveryProfilePlatform } from '../../../../services/report-creators';
import { ReportLanguageService } from '../../../../services/report-language.service';
import {
	reportDateLocale,
	reportMessage
} from '../../../../services/report-messages';

/** One card, worked out once from its creator so the template only reads. */
interface CreatorCardView {
	creator: ReportBlockCreator;
	key: string;
	name: string;
	/** `@handle`, or empty when the handle is all the name there is. */
	handle: string;
	platform: SocialListeningPlatformType;
	platformLabel: string;
	initials: string;
	avatarUrl: string | null;
	mentions: string;
	positive: string;
	negative: string;
	/** Percent of the card's mentions, for the sentiment bar. */
	positiveShare: number;
	negativeShare: number;
	/** A dash when the profile's count is not known: never a zero. */
	followers: string;
	/** In the editor, for a creator Discovery keeps a profile for. */
	openable: boolean;
}

/**
 * A Creator shortlist, drawn: one card per account, most mentions first, in
 * the style of the AI search's creator cards and the report's theme.
 *
 * Every figure comes from the backend's answer, read over the report's window
 * (`ReportBlockCreator`), and nothing is fetched here — the PDF's page stops
 * checking for changes after its first pass, so a card has to be whole the
 * moment it is drawn. The followers are the one figure that is not the
 * window's: they are the profile's, and the card says so under them.
 *
 * `editorMode` is the editor: there, a card whose creator Discovery knows is a
 * button, and `creatorOpened` asks the page to open that profile. Everywhere
 * else — the PDF, the shared page, present mode — the same card is drawn
 * with nothing to click.
 */
@Component({
	standalone: true,
	selector: 'app-report-creator-cards',
	templateUrl: './report-creator-cards.component.html',
	styleUrls: ['./report-creator-cards.component.scss'],
	imports: [
		NgFor,
		NgIf,
		NgTemplateOutlet,
		// matTooltip is silently inert without this.
		MatTooltipModule,
		TypographyComponent,
		SocialListeningPlatformIconComponent
	]
})
export class ReportCreatorCardsComponent {
	/** The shortlist's accounts, in the order the answer ranks them. */
	@Input() set creators(value: ReportBlockCreator[] | null | undefined) {
		this._creators = value ?? [];
		this.rebuild();
	}

	@Input() set editorMode(value: boolean) {
		this._editorMode = value;
		this.rebuild();
	}

	/** In the editor: the reader asked for this creator's Discovery profile. */
	@Output() creatorOpened = new EventEmitter<ReportBlockCreator>();

	cards: CreatorCardView[] = [];
	mentionsLabel = '';
	followersLabel = '';
	followersHint = '';
	positiveLabel = '';
	negativeLabel = '';

	private _creators: ReportBlockCreator[] = [];
	private _editorMode = false;
	private readonly shortNumber = new ShortNumberPipe();

	constructor(
		@Optional() private reportLanguage: ReportLanguageService | null
	) {}

	open(card: CreatorCardView): void {
		if (card.openable) {
			this.creatorOpened.emit(card.creator);
		}
	}

	/**
	 * A picture that will not load leaves the initials under it showing.
	 * Done on the element rather than through a flag, because the PDF's page
	 * no longer checks for changes by the time a late image fails.
	 */
	hideBrokenImage(event: Event): void {
		(event.target as HTMLElement).style.display = 'none';
	}

	trackByCard(_index: number, card: CreatorCardView): string {
		return card.key;
	}

	private rebuild(): void {
		const language = this.reportLanguage?.language;
		this.mentionsLabel = reportMessage(language, 'creators.mentions');
		this.followersLabel = reportMessage(language, 'creators.followers');
		this.followersHint = reportMessage(language, 'creators.followers.hint');
		this.positiveLabel = reportMessage(language, 'creators.positive');
		this.negativeLabel = reportMessage(language, 'creators.negative');
		const count = new Intl.NumberFormat(reportDateLocale(language));
		this.cards = this._creators.map(creator => this.cardOf(creator, count));
	}

	private cardOf(
		creator: ReportBlockCreator,
		count: Intl.NumberFormat
	): CreatorCardView {
		const name = (creator.name || creator.handle || '').trim();
		const bare = (creator.handle || '').replace(/^@/, '').trim();
		const mentions = Math.max(creator.mentions, 0);
		const openable =
			this._editorMode && discoveryProfilePlatform(creator) !== null;
		return {
			creator,
			key: `${creator.platform}:${bare}`,
			name,
			handle: bare && bare !== name ? `@${bare}` : '',
			platform: normalizeSocialListeningPlatform(creator.platform),
			platformLabel: formatPlatformLabel(creator.platform),
			initials: initialsOf(name),
			avatarUrl: creator.avatar_url || null,
			mentions: count.format(mentions),
			positive: count.format(creator.positive),
			negative: count.format(creator.negative),
			positiveShare: mentions ? (creator.positive / mentions) * 100 : 0,
			negativeShare: mentions ? (creator.negative / mentions) * 100 : 0,
			followers:
				creator.profile_followers == null
					? '—'
					: String(
							this.shortNumber.transform(
								creator.profile_followers
							)
					  ),
			openable
		};
	}
}

function initialsOf(name: string): string {
	return name
		.replace(/^@/, '')
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(word => word.charAt(0).toUpperCase())
		.join('');
}
